import { IonContent } from '@ionic/react';
import { useIonRouter } from '@ionic/react';
import { Button } from '@up2stay/up2promo-ui';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@up2stay/up2promo-ui';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import {
  AUTH_DESCRIPTION_CLASS,
  AuthLayoutWrapper,
} from '@/core/components/auth/auth-layout-wrapper';
import { useAuthFlow } from '@/core/hooks/use-auth-flow';
import { useAuthPublicConfig } from '@/core/hooks/use-auth-public-config';
import { AppIonPage } from '@/core/components/app-ion-page';

const RESEND_COOLDOWN_MS = 60_000;

export function VerifyPage() {
  const router = useIonRouter();
  const location = useLocation();
  const {
    verifyCode,
    sendLoginCode,
    commonDict,
    authLoginDict,
    authRegisterDict,
    errorsDict,
  } = useAuthFlow();
  const { registrationEnabled } = useAuthPublicConfig();

  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const emailFromParams = searchParams.get('email') || '';
  const codeFromParams = searchParams.get('code') || '';

  const [otp, setOtp] = useState<string>(() => codeFromParams);

  const [prevCodeFromParams, setPrevCodeFromParams] = useState(codeFromParams);
  if (
    codeFromParams &&
    codeFromParams.length === 6 &&
    codeFromParams !== prevCodeFromParams
  ) {
    setPrevCodeFromParams(codeFromParams);
    setOtp(codeFromParams);
  } else if (codeFromParams !== prevCodeFromParams) {
    setPrevCodeFromParams(codeFromParams);
  }

  const [isVerifying, setIsVerifying] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expiresAt, setExpiresAt] = useState<Date | null>(null);
  const [resendAvailableAt, setResendAvailableAt] = useState<number>(0);
  const [resendRemainingMs, setResendRemainingMs] = useState<number>(0);

  const processedCodeRef = useRef<string | null>(null);

  const handleVerify = useCallback(
    async (codeValue?: string) => {
      if (!emailFromParams) {
        setError('Please re-enter your email address to use the code.');
        router.push('/auth', 'forward', 'replace');
        return;
      }

      const codeToVerify = codeValue || otp;
      setIsVerifying(true);
      setError(null);

      const result = await verifyCode(emailFromParams, codeToVerify);

      if (result.success) {
        if (result.requiresCompletion && result.user) {
          const params = new URLSearchParams({
            email: emailFromParams,
            firstName: result.user.firstName || '',
            lastName: result.user.lastName || '',
            username: result.user.username || '',
          });
          router.push(
            `/auth/complete?${params.toString()}`,
            'forward',
            'replace',
          );
        }
      } else {
        setError(result.error || errorsDict.verificationFailed);
        // Clear the rejected code: a full field leaves no room for the next
        // iOS "From Mail" one-time-code suggestion, which inserts at the caret.
        setOtp('');
        setIsVerifying(false);
      }
    },
    [emailFromParams, errorsDict.verificationFailed, otp, router, verifyCode],
  );

  useEffect(() => {
    const shouldProcess =
      emailFromParams &&
      codeFromParams &&
      codeFromParams.length === 6 &&
      processedCodeRef.current !== codeFromParams;

    if (shouldProcess) {
      processedCodeRef.current = codeFromParams;
      setOtp(codeFromParams);
      void handleVerify(codeFromParams);
    }

    if (!codeFromParams && processedCodeRef.current) {
      processedCodeRef.current = null;
    }
  }, [codeFromParams, emailFromParams, handleVerify]);

  const handleChangeEmail = () => {
    router.push('/auth', 'forward', 'replace');
  };

  const handleResendCode = async () => {
    if (!emailFromParams) {
      router.push('/auth', 'forward', 'replace');
      return;
    }

    if (resendRemainingMs > 0) {
      return;
    }

    setIsLoading(true);
    setError(null);

    const result = await sendLoginCode(emailFromParams);

    if (result.success) {
      setOtp('');
      setExpiresAt(result.expiresAt || null);
      setResendAvailableAt(result.resendAvailableAt || 0);
      setResendRemainingMs(RESEND_COOLDOWN_MS);
    } else if (result.requiresRegistration) {
      if (registrationEnabled === false) {
        setError(authRegisterDict.registrationsDisabled);
        setIsLoading(false);
        return;
      }
      router.push(
        `/auth/register?email=${encodeURIComponent(emailFromParams)}`,
        'forward',
        'replace',
      );
    } else {
      setError(result.error || errorsDict.requestFailed);
    }

    setIsLoading(false);
  };

  const [currentTime, setCurrentTime] = useState(() => Date.now());

  useEffect(() => {
    if (!expiresAt) return;
    const interval = setInterval(() => {
      setCurrentTime(Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, [expiresAt]);

  const expiresInText = useMemo(() => {
    if (!expiresAt) return null;
    const diffMs = expiresAt.getTime() - currentTime;
    if (diffMs <= 0) return commonDict.codeExpired;
    const diffMinutes = Math.ceil(diffMs / 60000);
    return commonDict.codeExpiresIn.replace(
      '{minutes}',
      diffMinutes.toString(),
    );
  }, [expiresAt, currentTime, commonDict]);

  const resendCountdownText = useMemo(() => {
    if (resendRemainingMs <= 0) {
      return null;
    }
    const totalSeconds = Math.ceil(resendRemainingMs / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return minutes > 0
      ? `${minutes}:${seconds.toString().padStart(2, '0')}`
      : `${seconds}s`;
  }, [resendRemainingMs]);

  if (!resendAvailableAt && resendRemainingMs !== 0) {
    setResendRemainingMs(0);
  }

  useEffect(() => {
    if (!resendAvailableAt) return;
    if (typeof window === 'undefined') return;

    const updateCountdown = () => {
      const remaining = resendAvailableAt - Date.now();
      if (remaining <= 0) {
        setResendAvailableAt(0);
        setResendRemainingMs(0);
      } else {
        setResendRemainingMs(remaining);
      }
    };

    void queueMicrotask(updateCountdown);
    const interval = window.setInterval(updateCountdown, 1000);

    return () => {
      window.clearInterval(interval);
    };
  }, [resendAvailableAt]);

  return (
    <AppIonPage>
      <IonContent className="ion-padding" fullscreen scrollY={false}>
        <div className="bg-background text-foreground flex h-full w-full safe-area-padding">
          <AuthLayoutWrapper
            title={commonDict.otpTitle}
            description={commonDict.otpDescription}
            backTo="/auth"
          >
            <div className="flex flex-col gap-5">
              {emailFromParams && (
                <p className={AUTH_DESCRIPTION_CLASS}>
                  {commonDict.emailLabel}:{' '}
                  <span className="font-semibold text-foreground">
                    {emailFromParams}
                  </span>
                </p>
              )}
              {expiresInText && (
                <p className={AUTH_DESCRIPTION_CLASS}>{expiresInText}</p>
              )}

              <div className="flex flex-col items-center gap-2">
                <InputOTP
                  maxLength={6}
                  value={otp}
                  onChange={(value) => {
                    setOtp(value);
                    if (value.length === 6) {
                      void handleVerify(value);
                    }
                  }}
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  autoFocus
                  disabled={isVerifying}
                >
                  <InputOTPGroup>
                    <InputOTPSlot index={0} />
                    <InputOTPSlot index={1} />
                    <InputOTPSlot index={2} />
                    <InputOTPSlot index={3} />
                    <InputOTPSlot index={4} />
                    <InputOTPSlot index={5} />
                  </InputOTPGroup>
                </InputOTP>
                {error && (
                  <p className="text-center text-[13.5px] text-destructive">
                    {error}
                  </p>
                )}
              </div>

              <Button
                onClick={() => handleVerify()}
                variant="primary"
                size="lg"
                radius="full"
                className="w-full"
                disabled={otp.length !== 6}
                isLoading={isVerifying}
              >
                {authLoginDict.verify}
              </Button>

              <div className="flex items-center justify-between text-[13.5px]">
                <button
                  type="button"
                  onClick={handleResendCode}
                  disabled={isLoading || isVerifying || resendRemainingMs > 0}
                  className="font-medium text-[color:var(--ghost-accent-fg)] underline-offset-4 hover:underline disabled:opacity-50"
                >
                  {commonDict.resendCode}
                  {resendCountdownText && ` (${resendCountdownText})`}
                </button>
                <button
                  type="button"
                  onClick={handleChangeEmail}
                  className="text-muted-foreground underline-offset-4 hover:underline"
                >
                  {commonDict.changeEmail}
                </button>
              </div>
            </div>
          </AuthLayoutWrapper>
        </div>
      </IonContent>
    </AppIonPage>
  );
}
